import React from "react";
import { useServiceRequest } from "./context/ServiceRequestContext";
import SimpleInterviewSchedulerPOC from "./components/SimpleInterviewSchedulerPOC";
import SingleFileUploadRow from "../DocRepo/SingleFileUploadRow";
import Scheduler from "./scheduler/Scheduler";
import { useUserContext } from "../../context/UserContext";

interface RequestWorkflowSectionProps {
  serviceRequestId: string;
  role: "Admin" | "User";
  resumeUrl?: string | null;
  jdUrl?: string | null;
  reports?: string[];
  interviewType?: "JobDescription" | "SkillBased";
  onResumeChange: (newUrl: string | null) => void;
  onJDChange: (newUrl: string | null) => void;
  onReportsChange: (newReports: string[]) => void;
  onRefresh?: () => void;
}

const RequestWorkflowSection: React.FC<RequestWorkflowSectionProps> = ({
  serviceRequestId,
  role,
  interviewType = "JobDescription",
  onResumeChange,
  onJDChange,
  onRefresh,
}) => {
  const ServiceRequest = useServiceRequest();

  const docs = ServiceRequest?.details?.form_data?.Documents || {};
  const InterviewType =
    ServiceRequest?.details?.form_data?.Request?.["Request Details"]?.interview_type;

  const resumeUrl = docs?.Resume || null;
  const jdUrl = docs?.["Job Description"] || null;
  const isJDRequired = InterviewType === "Job Description Based";
  const [localResumeUrl, setLocalResumeUrl] = React.useState<string | null>(resumeUrl);
  const [localJDUrl, setLocalJDUrl] = React.useState<string | null>(jdUrl);
  const isResumeDone = !!resumeUrl;
  const isJDDone = !isJDRequired || !!jdUrl;
  const canSchedule = isResumeDone && isJDDone;
  const { user } = useUserContext();
  React.useEffect(() => {
    setLocalResumeUrl(resumeUrl);
  }, [resumeUrl]);

  React.useEffect(() => {
    setLocalJDUrl(jdUrl);
  }, [jdUrl]);
  const handleResumeChange = (url: string | null) => {
    setLocalResumeUrl(url);     // 🔥 immediate UI update
    onResumeChange(url);        // backend update
    onRefresh?.();              // refresh request details
  };

  const handleJDChange = (url: string | null) => {
    setLocalJDUrl(url);
    onJDChange(url);
    onRefresh?.();              // refresh request details
  };
  return (
    <div className="space-y-6">
      {/* Resume + JD side by side on large screens */}
      <div className="border p-4 rounded bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-center">
          {/* Upload Component */}
          <div className="lg:col-span-1">
            <SingleFileUploadRow
              collection_type="service_request"
              collection_ref={serviceRequestId}
              repo_type="RESUME"
              role={role}
              label="Resume"
              maxFileSizeKB={4096}
              fileUrl={localResumeUrl}
              onChange={handleResumeChange}
              uploadMode="final"
            />
          </div>
          {isJDRequired && (
            <div className="lg:col-span-1">
              <SingleFileUploadRow
                collection_type="service_request"
                collection_ref={serviceRequestId}
                repo_type="JD"
                role={role}
                label="Job Description"
                fileUrl={localJDUrl}
                onChange={handleJDChange}
                uploadMode="final"
              />
            </div>
          )}
        </div>
      </div>

      {/* Scheduler below */}
      <div className="border p-4 rounded bg-white">
        <h2 className="text-lg font-semibold mb-2">Interview Schedule</h2>
        {canSchedule ? (
          <Scheduler role={user?.role_name === "Admin" ? "Admin" : "User"} onRefresh={onRefresh} />
        ) : (
          <p className="p-4 bg-yellow-100 border rounded">
            Please upload the required documents to enable scheduling.
          </p>
        )}
      </div>
    </div >
  );
};

export default RequestWorkflowSection;